import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import SeoHead from "@/components/public/SeoHead";
import Breadcrumb from "@/components/public/Breadcrumb";
import FadeIn from "@/components/public/FadeIn";
import CTASection from "@/components/public/CTASection";
import DevisForm from "@/components/public/DevisForm";
import { Loader2, ChevronRight, AlertTriangle, CheckCircle, ArrowRight, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ANGLE_LABELS = { educatif: "Éducatif", comparatif: "Comparatif", warning: "Mise en garde", coût: "Coût" };
const ANGLE_COLORS = { educatif: "bg-blue-100 text-blue-700", comparatif: "bg-emerald-100 text-emerald-700", warning: "bg-amber-100 text-amber-700", coût: "bg-purple-100 text-purple-700" };

export default function BlogArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/blog/${slug}`),
      axios.get(`${API}/blog?limit=50`),
    ]).then(([a, all]) => {
      setArticle(a.data);
      const items = all.data.articles || all.data;
      const rel = items.filter(x =>
        x.slug !== slug && (x.service_name === a.data.service_name || x.ville_name === a.data.ville_name)
      ).slice(0, 4);
      setRelated(rel);
    }).catch(() => {
      navigate("/blog", { replace: true });
    }).finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading) return (
    <div className="animate-pulse">
      <div className="bg-[#F4F6F8] border-b border-[#062133]/10 py-3">
        <div className="max-w-4xl mx-auto px-4"><div className="h-4 w-48 bg-gray-200 rounded" /></div>
      </div>
      <div className="py-10 bg-white">
        <div className="max-w-4xl mx-auto px-4 space-y-4">
          <div className="flex gap-2"><div className="h-6 w-20 bg-gray-200 rounded" /><div className="h-6 w-16 bg-gray-200 rounded" /></div>
          <div className="h-10 w-3/4 bg-gray-200 rounded" />
          <div className="h-4 w-40 bg-gray-100 rounded" />
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 py-12 space-y-6">
        {[1,2,3,4].map(i => <div key={i} className="h-28 bg-gray-100 rounded-lg" />)}
      </div>
    </div>
  );
  if (!article) return null;

  const c = article.copywriting || {};
  const seoSlug = `${article.service_slug || article.service_name?.toLowerCase().replace(/ /g, "-")}-${article.ville_slug || article.ville_name?.toLowerCase().replace(/ /g, "-")}`;
  const serviceSlug = article.service_slug || article.service_name?.toLowerCase().replace(/ /g, "-");
  const dateStr = (article.real_published_date || article.published_date || article.created_at) ? new Date(article.real_published_date || article.published_date || article.created_at).toLocaleDateString("fr-CH", { day: "numeric", month: "long", year: "numeric" }) : "";

  const schemaLD = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.meta_description,
    "author": { "@type": "Organization", "name": "Nettoyage Expert", "url": "https://www.nettoyage-expert.ch" },
    "publisher": { "@type": "Organization", "name": "Nettoyage Expert", "logo": { "@type": "ImageObject", "url": "https://www.nettoyage-expert.ch/logo-black.png" } },
    "datePublished": article.real_published_date || article.published_date || article.created_at || "",
    "dateModified": article.real_published_date || article.published_date || article.created_at || "",
  };

  return (
    <div data-testid="blog-article-page">
      <SeoHead
        title={article.meta_title}
        description={article.meta_description}
        path={`/blog/${article.slug}`}
        type="article"
        breadcrumbs={[
          { name: "Accueil", url: "/" },
          { name: "Blog", url: "/blog" },
          { name: article.title }
        ]}
        schemas={[schemaLD]}
      />
      <Breadcrumb items={[{ name: "Accueil", url: "/" }, { name: "Blog", url: "/blog" }, { name: article.title }]} />

      {/* Article Header */}
      <section className="py-10 sm:py-14 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="text-xs font-medium bg-[#336DBF]/10 text-[#336DBF] px-2.5 py-1 rounded">{article.service_name}</span>
              {article.ville_name && article.ville_name !== "Suisse romande" && (
                <span className="text-xs font-medium bg-[#F4F6F8] text-[#062133]/60 px-2.5 py-1 rounded">{article.ville_name}</span>
              )}
              {article.type_client && article.type_client !== "tous" && (
                <span className="text-xs font-medium bg-[#F4F6F8] text-[#062133]/60 px-2.5 py-1 rounded">{article.type_client === "particulier" ? "Particulier" : "Entreprise"}</span>
              )}
              <span className={`text-xs font-medium px-2.5 py-1 rounded ${ANGLE_COLORS[article.angle] || "bg-gray-100 text-gray-600"}`}>
                {ANGLE_LABELS[article.angle] || article.angle}
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#062133] tracking-tight leading-tight" data-testid="blog-article-h1">
              {article.title}
            </h1>
            <p className="mt-3 text-sm text-[#062133]/60">Par Nettoyage Expert {dateStr && `\u2022 ${dateStr}`}</p>
          </FadeIn>
        </div>
      </section>

      {/* Article Body */}
      <article className="pb-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* HTML body for editorial articles */}
          {c.html_body ? (
            <FadeIn>
              <div
                className="max-w-none [&_h2]:text-xl [&_h2]:sm:text-2xl [&_h2]:font-semibold [&_h2]:text-[#062133] [&_h2]:tracking-tight [&_h2]:mt-10 [&_h2]:mb-4 [&_p]:text-[#062133]/70 [&_p]:leading-relaxed [&_p]:mb-4 [&_a]:text-[#336DBF] [&_a]:font-medium hover:[&_a]:underline [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ul]:space-y-2 [&_li]:text-[#062133]/70 [&_strong]:text-[#062133]"
                dangerouslySetInnerHTML={{ __html: c.html_body }}
                data-testid="blog-html-body"
              />
              {c.signature && (
                <p className="mt-10 text-xs text-[#062133]/40 italic" data-testid="blog-signature"
                   dangerouslySetInnerHTML={{ __html: c.signature }} />
              )}
            </FadeIn>
          ) : (
          <>
          {/* Intro */}
          {c.intro && (
            <FadeIn>
              <p className="text-[#062133] text-lg leading-relaxed mb-10" data-testid="blog-intro">{c.intro}</p>
            </FadeIn>
          )}

          {/* Risques (master articles) */}
          {c.risques && (
            <FadeIn>
              <div className="mb-10" data-testid="blog-risques">
                <h2 className="text-xl sm:text-2xl font-semibold text-[#062133] tracking-tight mb-4">Les risques à connaître</h2>
                <p className="text-[#062133]/60 text-base leading-relaxed">{c.risques}</p>
              </div>
            </FadeIn>
          )}

          {/* Problème local (local articles) */}
          {c.probleme_local && (
            <FadeIn>
              <div className="mb-10" data-testid="blog-probleme-local">
                <h2 className="text-xl sm:text-2xl font-semibold text-[#062133] tracking-tight mb-4">Le contexte local</h2>
                <p className="text-[#062133]/60 text-base leading-relaxed">{c.probleme_local}</p>
              </div>
            </FadeIn>
          )}

          <CTASection title="Besoin d'un devis rapide ?" subtitle="Réponse garantie sous 2 heures. Sans engagement." />

          {/* Erreurs (master articles) */}
          {c.erreurs && (
            <FadeIn>
              <div className="my-10" data-testid="blog-erreurs">
                <h2 className="text-xl sm:text-2xl font-semibold text-[#062133] tracking-tight mb-4">Les erreurs les plus fréquentes</h2>
                <p className="text-[#062133]/60 text-base leading-relaxed">{c.erreurs}</p>
              </div>
            </FadeIn>
          )}

          {/* Intervention (local articles) */}
          {c.intervention && (
            <FadeIn>
              <div className="my-10" data-testid="blog-intervention">
                <h2 className="text-xl sm:text-2xl font-semibold text-[#062133] tracking-tight mb-4">Notre intervention professionnelle</h2>
                <p className="text-[#062133]/60 text-base leading-relaxed">{c.intervention}</p>
              </div>
            </FadeIn>
          )}

          {/* Solutions */}
          {c.solutions && (
            <FadeIn>
              <div className="mb-10" data-testid="blog-solutions">
                <h2 className="text-xl sm:text-2xl font-semibold text-[#062133] tracking-tight mb-4">Les solutions professionnelles</h2>
                <p className="text-[#062133]/60 text-base leading-relaxed">{c.solutions}</p>
              </div>
            </FadeIn>
          )}

          {/* Comparaison */}
          {c.comparaison && (
            <FadeIn>
              <div className="mb-10" data-testid="blog-comparaison">
                <h2 className="text-xl sm:text-2xl font-semibold text-[#062133] tracking-tight mb-4">Comparaison : professionnel vs amateur</h2>
                <p className="text-[#062133]/60 text-base leading-relaxed">{c.comparaison}</p>
              </div>
            </FadeIn>
          )}

          <CTASection title="Comparez par vous-même" subtitle="Demandez un devis gratuit et découvrez la différence" />

          {/* Résultats concrets (local articles) */}
          {c.resultats && (
            <FadeIn>
              <div className="my-10 bg-emerald-50 border border-emerald-200 rounded-lg p-6" data-testid="blog-resultats">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <h2 className="text-xl sm:text-2xl font-semibold text-[#062133] tracking-tight mb-3">Les résultats concrets</h2>
                    <p className="text-[#062133]/60 text-base leading-relaxed">{c.resultats}</p>
                  </div>
                </div>
              </div>
            </FadeIn>
          )}

          {/* Entreprises à éviter */}
          {c.entreprises_a_eviter && (
            <FadeIn>
              <div className="my-10 bg-amber-50 border border-amber-200 rounded-lg p-6" data-testid="blog-eviter">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <h2 className="text-xl sm:text-2xl font-semibold text-[#062133] tracking-tight mb-3">Pourquoi certaines entreprises sont à éviter</h2>
                    <p className="text-[#062133]/60 text-base leading-relaxed">{c.entreprises_a_eviter}</p>
                  </div>
                </div>
              </div>
            </FadeIn>
          )}

          {/* Reconnaitre pro */}
          {c.reconnaitre_pro && (
            <FadeIn>
              <div className="mb-10 bg-emerald-50 border border-emerald-200 rounded-lg p-6" data-testid="blog-reconnaitre">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <h2 className="text-xl sm:text-2xl font-semibold text-[#062133] tracking-tight mb-3">Comment reconnaître un vrai professionnel</h2>
                    <p className="text-[#062133]/60 text-base leading-relaxed">{c.reconnaitre_pro}</p>
                  </div>
                </div>
              </div>
            </FadeIn>
          )}

          {/* FAQ */}
          {c.faq && c.faq.length > 0 && (
            <FadeIn>
              <div className="mb-10" data-testid="blog-faq">
                <h2 className="text-xl sm:text-2xl font-semibold text-[#062133] tracking-tight mb-6">Questions fréquentes</h2>
                <div className="space-y-3">
                  {c.faq.map((item, i) => (
                    <details key={i} className="group bg-[#F4F6F8] border border-[#062133]/10 rounded-lg" data-testid={`blog-faq-item-${i}`}>
                      <summary className="flex items-center justify-between cursor-pointer p-4 sm:p-5 list-none [&::-webkit-details-marker]:hidden">
                        <span className="text-[#062133] font-medium pr-4 text-sm sm:text-base">{item.q}</span>
                        <ChevronDown className="w-4 h-4 text-[#336DBF] shrink-0 transition-transform group-open:rotate-180" />
                      </summary>
                      <div className="px-4 sm:px-5 pb-4 sm:pb-5 pt-0">
                        <p className="text-[#062133]/60 text-sm leading-relaxed">{item.r}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            </FadeIn>
          )}

          {/* Internal links */}
          <FadeIn>
            <div className="mb-10 flex flex-wrap gap-3" data-testid="blog-internal-links">
              {c.vitrine_slug && (
                <Link
                  to={`/${c.vitrine_slug}`}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#336DBF] text-white rounded-md text-sm font-medium hover:bg-[#2A5A9E] transition-colors"
                  data-testid="blog-link-vitrine"
                >
                  Voir notre page locale <ArrowRight size={14} />
                </Link>
              )}
              <Link
                to={`/services/${serviceSlug}`}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#336DBF]/10 text-[#336DBF] rounded-md text-sm font-medium hover:bg-[#336DBF]/20 transition-colors"
                data-testid="blog-link-service"
              >
                {article.service_name} <ArrowRight size={14} />
              </Link>
            </div>
          </FadeIn>
          </>
          )}
        </div>
      </article>

      {/* DevisForm */}
      <section className="py-16 sm:py-24 bg-[#F4F6F8]" data-testid="blog-devis-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-8">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Contact</p>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight">
                {c.cta || "Demandez votre devis gratuit"}
              </h2>
              <p className="mt-2 text-[#062133]/60">Réponse garantie sous 2 heures</p>
            </div>
            <div className="bg-white border border-[#062133]/10 rounded-lg p-6 sm:p-8 shadow-sm">
              <DevisForm source="blog" prefilledService={article.service_name} prefilledVille={article.ville_name} />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Related articles */}
      {related.length > 0 && (
        <section className="py-16 sm:py-24 bg-white" data-testid="blog-related">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">A lire aussi</p>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-8">Articles similaires</h2>
            </FadeIn>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {related.map((r, i) => (
                <FadeIn key={r.slug} delay={i * 80}>
                  <Link to={`/blog/${r.slug}`} className="block bg-[#F4F6F8] border border-[#062133]/10 rounded-lg p-4 hover:border-[#336DBF]/30 hover:shadow-sm transition-all">
                    <h3 className="text-sm font-semibold text-[#062133] line-clamp-2 mb-2">{r.title}</h3>
                    <div className="flex gap-1.5">
                      <span className="text-xs bg-[#336DBF]/10 text-[#336DBF] px-1.5 py-0.5 rounded">{r.ville_name}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${ANGLE_COLORS[r.angle] || ""}`}>{ANGLE_LABELS[r.angle]}</span>
                    </div>
                  </Link>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
